import { Injectable } from '@angular/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { ProgramItemsWithGroup } from '../models/program-items-with-group.model';
import { ProgramItemState } from '../models/program-item.state';
import {
    CreateProgramItem,
    CreateProgramItemFailure,
    CreateProgramItemSuccess,
    DeleteProgramItem,
    DeleteProgramItemFailure,
    DeleteProgramItemSuccess,
    FetchProgramItemsWithGroup,
    FetchProgramItemsWithGroupFailure,
    FetchProgramItemsWithGroupSuccess,
    UpdateProgramItem,
    UpdateProgramItemFailure,
    UpdateProgramItemSuccess,
    AddCourseOfferingsToCourseCategorySuccess,
    AddCourseOfferingsToCourseCategoryFailure,
    AddCourseOfferingsToCourseCategory,
    ChangeProgramCompletionForParticipants,
    ChangeProgramCompletionForParticipantsSuccess,
    ChangeProgramCompletionForParticipantsFailure,
    FetchProgramItemsByCategorySearch,
    FetchProgramItemsByCategorySearchSuccess,
    FetchProgramItemsByCategorySearchFailure,
    FetchAssessmentCourses,
    FetchAssessmentCoursesSuccess,
    FetchAssessmentCoursesFailure,
    FetchProgramExecutionStatistics,
    FetchProgramExecutionStatisticsSuccess,
    FetchProgramExecutionStatisticsFailure,
    FetchParticipantProgramExecution,
    FetchParticipantProgramExecutionSuccess,
    FetchParticipantProgramExecutionFailure,
    FetchProgramExecutionMetrics,
    FetchProgramExecutionMetricsSuccess,
    FetchProgramExecutionMetricsFailure,
    FetchRelevantExecutionsDateRange,
    FetchRelevantExecutionsDateRangeSuccess,
    FetchRelevantExecutionsDateRangeFailure,
    UpdateRelevantExecutionsDateRange,
    UpdateRelevantExecutionsDateRangeSuccess,
    UpdateRelevantExecutionsDateRangeFailure,
    DeleteRelevantExecutionsDateRange,
    DeleteRelevantExecutionsDateRangeSuccess,
    DeleteRelevantExecutionsDateRangeFailure,
    UpdateProgramItemRelevantExecutionRule,
    UpdateProgramItemRelevantExecutionRuleSuccess,
    UpdateProgramItemRelevantExecutionRuleFailure,
    FetchProgramExecutionStructureWithDefaultExecutions,
    FetchProgramExecutionStructureWithDefaultExecutionsSuccess,
    FetchProgramExecutionStructureWithDefaultExecutionsFailure,
    FetchProgramExecutionCourseOfferingRelevantExecutions,
    FetchProgramExecutionCourseOfferingRelevantExecutionsSuccess,
    FetchProgramExecutionCourseOfferingRelevantExecutionsFailure,
    FetchProgramParticipantExecutionChanges,
    FetchProgramParticipantExecutionChangesSuccess,
    FetchProgramParticipantExecutionChangesFailure,
    FetchParticipantProgramExecutionGroup,
    FetchParticipantProgramExecutionGroupSuccess,
    FetchParticipantProgramExecutionGroupFailure,
    FetchTeamParticipantProgramExecutionGroup,
    FetchTeamParticipantProgramExecutionGroupSuccess,
    FetchTeamParticipantProgramExecutionGroupFailure,
    FetchTeamParticipantProgramExecution,
    FetchTeamParticipantProgramExecutionSuccess,
    FetchTeamParticipantProgramExecutionFailure,
} from '../actions';
import { ICreateProgramItemActionPayload } from '../interfaces/create-program-item.action.payload.interface';
import { ProgramItem } from '../models/program-item.model';
import { filter, map, take } from 'rxjs/operators';
import { BehaviorSubject, race, Subject } from 'rxjs';
import { IAddCourseOfferingsToCourseCategoryActionPayload } from '../interfaces/add-course-offerings-to-course-category.action.payload.interface';
import { IChangeProgramCompletionForParticipantsActionPayload } from '../interfaces/change-program-completion-for-participants.action.payload.interface';
import { IChangeProgramCompletionResult } from '../interfaces/change-program-completion-result.interface';
import { AssessmentCourse } from '../models/assessment-course.model';
import { ProgramStructureWithProgramParticipant } from '../models/program-structure-with-program-participant.model';
import { ProgramExecutionMetrics } from '../models/program-execution-metrics.model';
import { DateRange } from '../../../projects/shared/src/lib/models/date-range.model';
import { RelevantExecutionRuleEnum } from 'app/library/relevant-execution/enums/relevant-execution-rule.enum';
import { ProgramStructureWithGroup } from '../models/program-structure-with-group.model';
import { CourseRelevantExecution } from 'app/library/relevant-execution/models/course-relevant-execution.model';
import { ParticipantExecutionChange } from '../models/participant-execution-change.model';
import { IParticipantExecutionChangeActionPayload } from '../interfaces/participant-execution-change-action.action-payload.interface';
import { PartcipantCourseGroup } from 'app/library/program-item/models/participant-course-group.model';

@Injectable()
export class ProgramItemService {
    private _programItemsByCategorySubject = new BehaviorSubject<ProgramItemsWithGroup[]>(null);
    public programItemsByCategorySubject$ = this._programItemsByCategorySubject.asObservable();

    private _programStructureStatisticsSubject = new Subject<ProgramItemsWithGroup[]>();
    public programStructureStatisticsSubject$ = this._programStructureStatisticsSubject.asObservable();

    private _programStructureMetricsSubject = new BehaviorSubject<ProgramExecutionMetrics>(null);
    public programStructureMetricsSubject$ = this._programStructureMetricsSubject.asObservable();

    private _programStructureWithProgramParticipantSubject = new BehaviorSubject<ProgramStructureWithProgramParticipant>(null);
    public programStructureWithProgramParticipantSubject$ = this._programStructureWithProgramParticipantSubject.asObservable();

    private _relevantExecutionsDateRangeSubject = new BehaviorSubject<DateRange>(null);
    public relevantExecutionsDateRangeSubject$ = this._relevantExecutionsDateRangeSubject.asObservable();

    constructor(private _store: Store, private _actionHelper: NgxsActionHelper, private _action$: Actions) {}

    getCourseOfferingById(courseOfferingId: string): ProgramItem {
        return ProgramItemState.getEntity(this._store.selectSnapshot(ProgramItemState), courseOfferingId);
    }

    async fetchProgramItemsWithGroup(categoryId: string, tags?: number[], searchTerm?: string): Promise<ProgramItemsWithGroup[]> {
        this._store.dispatch(new FetchProgramItemsWithGroup({ categoryId, tags, searchTerm }));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchProgramItemsWithGroupSuccess),
                map((action: FetchProgramItemsWithGroupSuccess) => {
                    this._programItemsByCategorySubject.next(action.payload);
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramItemsWithGroupFailure),
                map((action: FetchProgramItemsWithGroupFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchProgramItemsByCategorySearch(moduleId: string, tags?: number[], searchTerm?: string): Promise<ProgramItemsWithGroup[]> {
        this._store.dispatch(new FetchProgramItemsByCategorySearch({ moduleId, tags, searchTerm }));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchProgramItemsByCategorySearchSuccess),
                map((action: FetchProgramItemsByCategorySearchSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramItemsByCategorySearchFailure),
                map((action: FetchProgramItemsByCategorySearchFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchAssessmentCourses(moduleId: string, tags?: number[], searchTerm?: string): Promise<AssessmentCourse[]> {
        this._store.dispatch(new FetchAssessmentCourses({ moduleId, tags, searchTerm }));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchAssessmentCoursesSuccess),
                map((action: FetchAssessmentCoursesSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAssessmentCoursesFailure),
                map((action: FetchAssessmentCoursesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createCourseOffering(courseOffering: ICreateProgramItemActionPayload): Promise<boolean> {
        this._store.dispatch(new CreateProgramItem(courseOffering));

        return await this._actionHelper.race(CreateProgramItemSuccess, CreateProgramItemFailure).toPromise();
    }

    async updateProgramItem(programItem: ProgramItem): Promise<boolean> {
        this._store.dispatch(new UpdateProgramItem(programItem));

        return await this._actionHelper.race(UpdateProgramItemSuccess, UpdateProgramItemFailure).toPromise();
    }

    async deleteCourseOffering(courseOfferingId: string): Promise<boolean> {
        this._store.dispatch(new DeleteProgramItem(courseOfferingId));

        return await this._actionHelper.race(DeleteProgramItemSuccess, DeleteProgramItemFailure).toPromise();
    }

    async addCourseOfferingsToCourseCategory(payload: IAddCourseOfferingsToCourseCategoryActionPayload): Promise<boolean> {
        this._store.dispatch(new AddCourseOfferingsToCourseCategory(payload));

        return await this._actionHelper.race(AddCourseOfferingsToCourseCategorySuccess, AddCourseOfferingsToCourseCategoryFailure).toPromise();
    }

    async changeProgramCompletionForParticipants(payload: IChangeProgramCompletionForParticipantsActionPayload): Promise<IChangeProgramCompletionResult | false> {
        this._store.dispatch(new ChangeProgramCompletionForParticipants(payload));

        return await race(
            this._action$.pipe(
                ofActionDispatched(ChangeProgramCompletionForParticipantsSuccess),
                map((action: ChangeProgramCompletionForParticipantsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(ChangeProgramCompletionForParticipantsFailure),
                map(() => false as const)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchProgramExecutionStatistics(programExecutionId: string, offeringIds: string[]): Promise<ProgramItemsWithGroup[]> {
        const offeringIdsHash = this._generateHash(offeringIds);
        this._store.dispatch(new FetchProgramExecutionStatistics(programExecutionId, offeringIds));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchProgramExecutionStatisticsSuccess),
                filter((action: FetchProgramExecutionStatisticsSuccess) => action.programExecutionId === programExecutionId && action.offeringIdsHash === offeringIdsHash),
                map((action) => {
                    this._programStructureStatisticsSubject.next(action.payload);
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramExecutionStatisticsFailure),
                filter((action: FetchProgramExecutionStatisticsFailure) => action.programExecutionId === programExecutionId && action.offeringIdsHash === offeringIdsHash),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    private _generateHash(offeringIds: string[]): string {
        return offeringIds.sort().join(','); // or use a real hash if needed
    }

    async fetchProgramExecutionMetrics(id: string): Promise<ProgramItemsWithGroup> {
        this._store.dispatch(new FetchProgramExecutionMetrics(id));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchProgramExecutionMetricsSuccess),
                map((action: FetchProgramExecutionMetricsSuccess) => {
                    this._programStructureMetricsSubject.next(action.payload);
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramExecutionMetricsFailure),
                map((action: FetchProgramExecutionMetricsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchRelevantExecutionsDateRange(id: string): Promise<ProgramItemsWithGroup> {
        this._store.dispatch(new FetchRelevantExecutionsDateRange(id));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchRelevantExecutionsDateRangeSuccess),
                map((action: FetchRelevantExecutionsDateRangeSuccess) => {
                    this._relevantExecutionsDateRangeSubject.next(action.payload);
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchRelevantExecutionsDateRangeFailure),
                map((action: FetchRelevantExecutionsDateRangeFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updateRelevantExecutionsDateRange(programExecutionId: string, dateRange: DateRange): Promise<boolean> {
        this._store.dispatch(new UpdateRelevantExecutionsDateRange({ programExecutionId, dateRange }));

        return await this._actionHelper.race(UpdateRelevantExecutionsDateRangeSuccess, UpdateRelevantExecutionsDateRangeFailure).toPromise();
    }

    async updateProgramItemRelevantExecutionRule(programItemId: string, relevantExecutionRule: RelevantExecutionRuleEnum): Promise<boolean> {
        this._store.dispatch(new UpdateProgramItemRelevantExecutionRule({ programItemId, relevantExecutionRule }));

        return await this._actionHelper.race(UpdateProgramItemRelevantExecutionRuleSuccess, UpdateProgramItemRelevantExecutionRuleFailure).toPromise();
    }

    async removeRelevantExecutionsDateRange(programExecutionId: string): Promise<boolean> {
        this._store.dispatch(new DeleteRelevantExecutionsDateRange(programExecutionId));

        return await this._actionHelper.race(DeleteRelevantExecutionsDateRangeSuccess, DeleteRelevantExecutionsDateRangeFailure).toPromise();
    }

    async fetchParticipantProgramExecution(programCourseId: string, userId: string): Promise<boolean> {
        this._store.dispatch(new FetchParticipantProgramExecution(programCourseId, userId));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchParticipantProgramExecutionSuccess),
                map((action: FetchParticipantProgramExecutionSuccess) => {
                    this._programStructureWithProgramParticipantSubject.next(action.payload);
                    return true;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchParticipantProgramExecutionFailure),
                map((action: FetchParticipantProgramExecutionFailure) => false)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamParticipantProgramExecution(teamId: string, programCourseId: string, userId: string): Promise<boolean> {
        this._store.dispatch(new FetchTeamParticipantProgramExecution(teamId, programCourseId, userId));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchTeamParticipantProgramExecutionSuccess),
                map((action: FetchTeamParticipantProgramExecutionSuccess) => {
                    this._programStructureWithProgramParticipantSubject.next(action.payload);
                    return true;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamParticipantProgramExecutionFailure),
                map((action: FetchTeamParticipantProgramExecutionFailure) => false)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchParticipantProgramExecutionGroup(programCourseId: string, courseGroupId: string, userId: string): Promise<PartcipantCourseGroup> {
        this._store.dispatch(new FetchParticipantProgramExecutionGroup(programCourseId, courseGroupId, userId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchParticipantProgramExecutionGroupSuccess),
                filter((action: FetchParticipantProgramExecutionGroupSuccess) => action.programCourseId === programCourseId && action.courseGroupId === courseGroupId),
                map((action) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchParticipantProgramExecutionGroupFailure),
                filter((action: FetchParticipantProgramExecutionGroupFailure) => action.programCourseId === programCourseId && action.courseGroupId === courseGroupId),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchTeamParticipantProgramExecutionGroup(teamId: string, programCourseId: string, courseGroupId: string, userId: string): Promise<PartcipantCourseGroup> {
        this._store.dispatch(new FetchTeamParticipantProgramExecutionGroup(teamId, programCourseId, courseGroupId, userId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTeamParticipantProgramExecutionGroupSuccess),
                filter((action: FetchTeamParticipantProgramExecutionGroupSuccess) => action.programCourseId === programCourseId && action.courseGroupId === courseGroupId),
                map((action) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTeamParticipantProgramExecutionGroupFailure),
                filter((action: FetchTeamParticipantProgramExecutionGroupFailure) => action.programCourseId === programCourseId && action.courseGroupId === courseGroupId),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchProgramExecutionStructureWithDefaultExecutions(programExecutionId: string): Promise<ProgramStructureWithGroup[]> {
        this._store.dispatch(
            new FetchProgramExecutionStructureWithDefaultExecutions({
                programExecutionId: programExecutionId,
            })
        );

        return race(
            this._action$.pipe(
                filter((courseOfferingExecutions) => !!courseOfferingExecutions),
                ofActionDispatched(FetchProgramExecutionStructureWithDefaultExecutionsSuccess),
                map((action: FetchProgramExecutionStructureWithDefaultExecutionsSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramExecutionStructureWithDefaultExecutionsFailure),
                map((action: FetchProgramExecutionStructureWithDefaultExecutionsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchProgramExecutionCourseOfferingRelevantExecutions(
        programExecutionId: string,
        courseOfferingId: string,
        context?: string
    ): Promise<CourseRelevantExecution[]> {
        this._store.dispatch(
            new FetchProgramExecutionCourseOfferingRelevantExecutions({
                programExecutionId: programExecutionId,
                courseOfferingId: courseOfferingId,
                context,
            })
        );

        return race(
            this._action$.pipe(
                filter((courseOfferingRelevantExecutions) => !!courseOfferingRelevantExecutions),
                ofActionDispatched(FetchProgramExecutionCourseOfferingRelevantExecutionsSuccess),
                map((action: FetchProgramExecutionCourseOfferingRelevantExecutionsSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramExecutionCourseOfferingRelevantExecutionsFailure),
                map((action: FetchProgramExecutionCourseOfferingRelevantExecutionsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchParticipantExecutionChanges(payload: IParticipantExecutionChangeActionPayload): Promise<ParticipantExecutionChange[]> {
        this._store.dispatch(new FetchProgramParticipantExecutionChanges(payload));

        return race(
            this._action$.pipe(
                filter((participantExecutionChanges) => !!participantExecutionChanges),
                ofActionDispatched(FetchProgramParticipantExecutionChangesSuccess),
                map((action: FetchProgramParticipantExecutionChangesSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramParticipantExecutionChangesFailure),
                map((action: FetchProgramParticipantExecutionChangesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }
}
